import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { auditLogService } from '@/services/auditLogService';
import type { AuditLogEntry, AuditEventType } from '@/types/members';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const EVENT_TYPE_LABELS: Record<AuditEventType, string> = {
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  get LOGIN_FAILED() { return translateInterfaceText("Login Falhou"); },
  get TOKEN_REFRESH() { return translateInterfaceText("Refresh Token"); },
  get ROUTE_ACCESS() { return translateInterfaceText("Acesso Rota"); },
  get PAYMENT_CREATED() { return translateInterfaceText("Pagamento Criado"); },
  get PAYMENT_UPDATED() { return translateInterfaceText("Pagamento Atualizado"); },
  get PAYMENT_FAILED() { return translateInterfaceText("Pagamento Falhou"); },
  get MEMBER_CREATED() { return translateInterfaceText("Membro Criado"); },
  get MEMBER_UPDATED() { return translateInterfaceText("Membro Atualizado"); },
  get MEMBER_REMOVED() { return translateInterfaceText("Membro Removido"); },
  get PERMISSION_CHANGED() { return translateInterfaceText("Permissão Alterada"); },
  get TOKEN_REVOKED() { return translateInterfaceText("Token Revogado"); },
  get PASSWORD_CHANGED() { return translateInterfaceText("Senha Alterada"); },
};

const EVENT_TYPE_COLORS: Record<AuditEventType, string> = {
  LOGIN: 'bg-green-100 text-green-700',
  LOGOUT: 'bg-gray-100 text-gray-600',
  LOGIN_FAILED: 'bg-red-100 text-red-700',
  TOKEN_REFRESH: 'bg-blue-100 text-blue-700',
  ROUTE_ACCESS: 'bg-slate-100 text-slate-600',
  PAYMENT_CREATED: 'bg-emerald-100 text-emerald-700',
  PAYMENT_UPDATED: 'bg-amber-100 text-amber-700',
  PAYMENT_FAILED: 'bg-red-100 text-red-700',
  MEMBER_CREATED: 'bg-indigo-100 text-indigo-700',
  MEMBER_UPDATED: 'bg-purple-100 text-purple-700',
  MEMBER_REMOVED: 'bg-rose-100 text-rose-700',
  PERMISSION_CHANGED: 'bg-orange-100 text-orange-700',
  TOKEN_REVOKED: 'bg-red-100 text-red-700',
  PASSWORD_CHANGED: 'bg-cyan-100 text-cyan-700',
};

export default function AuditLogs() {
  useLanguage();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(25);

  // Filters
  const [emailFilter, setEmailFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [searchTriggered, setSearchTriggered] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const result = await auditLogService.list({
        page,
        pageSize,
        email: emailFilter.trim() || undefined,
        event_type: (eventTypeFilter as AuditEventType) || undefined,
      });
      setLogs(result.data);
      setTotal(result.pagination.total);
    } catch (error: unknown) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, emailFilter, eventTypeFilter, searchTriggered]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    setPage(1);
    setSearchTriggered((prev) => prev + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString(getInterfaceLanguage(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <div className="hidden lg:block h-px bg-border w-full" />
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 max-w-7xl">
            {/* Header */}
            <div className="pb-5 border-b border-[#E5E7EB]">
              <h1 className="text-[22px] font-semibold text-[#0A1A2F] tracking-tight">
                {translateInterfaceText("Auditoria")}</h1>
              <p className="text-[14px] text-[#9CA3AF] font-normal mt-1">
                {translateInterfaceText("Histórico de ações e eventos do sistema.")}</p>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 pt-5 mb-6">
              <div className="flex-1">
                <Label htmlFor="emailFilter" className="text-sm font-medium text-[#475569] mb-1 block">
                  {translateInterfaceText("Buscar por e-mail")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="emailFilter"
                    type="email"
                    placeholder="exemplo@email.com"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-11 rounded-[10px] flex-1"
                  />
                  <Button
                    onClick={handleSearch}
                    variant="outline"
                    className="h-11 rounded-[10px] border-[#E2E8F0]"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="w-full sm:w-48">
                <Label className="text-sm font-medium text-[#475569] mb-1 block">
                  {translateInterfaceText("Tipo de evento")}</Label>
                <Select
                  value={eventTypeFilter}
                  onValueChange={(v) => {
                    setEventTypeFilter(v === 'all' ? '' : v);
                    setPage(1);
                    setSearchTriggered((prev) => prev + 1);
                  }}
                >
                  <SelectTrigger className="h-11 rounded-[10px]">
                    <SelectValue placeholder={translateInterfaceText("Todos")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{translateInterfaceText("Todos")}</SelectItem>
                    {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#556ff8]" />
                <span className="ml-2 text-sm text-[#64748B]">{translateInterfaceText("Carregando logs...")}</span>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-[#64748B]">
                  {translateInterfaceText("Nenhum log encontrado com os filtros selecionados.")}</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="bg-white rounded-lg border border-[#E6EAF0] overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-[1.5fr_2fr_1.5fr_1fr_1.5fr] gap-4 px-6 py-3 bg-[#F8FAFC] border-b border-[#E6EAF0]">
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                      {translateInterfaceText("Evento")}</div>
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                      {translateInterfaceText("Descrição")}</div>
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                      {translateInterfaceText("Usuário")}</div>
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                      {translateInterfaceText("IP")}</div>
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                      {translateInterfaceText("Data/Hora")}</div>
                  </div>

                  {/* Body */}
                  <div>
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className="grid grid-cols-[1.5fr_2fr_1.5fr_1fr_1.5fr] gap-4 px-6 py-4 border-b border-[#E6EAF0] last:border-b-0 hover:bg-[#FAFBFC] transition-colors"
                      >
                        {/* Event Type */}
                        <div className="flex items-center">
                          <Badge
                            className={`${EVENT_TYPE_COLORS[log.event_type] || 'bg-gray-100 text-gray-600'} text-xs font-medium`}
                          >
                            {EVENT_TYPE_LABELS[log.event_type] || log.event_type}
                          </Badge>
                        </div>

                        {/* Description */}
                        <div className="flex items-center">
                          <span className="text-sm text-[#1E293B] truncate">
                            {log.description || '-'}
                          </span>
                        </div>

                        {/* User */}
                        <div className="flex items-center">
                          <span className="text-sm text-[#64748B] truncate">
                            {log.metadata?.email || log.metadata?.member_email || log.user_id || '-'}
                          </span>
                        </div>

                        {/* IP */}
                        <div className="flex items-center">
                          <span className="text-sm text-[#64748B] truncate">
                            {log.ip_address || '-'}
                          </span>
                        </div>

                        {/* Date */}
                        <div className="flex items-center">
                          <span className="text-sm text-[#64748B]">
                            {formatDate(log.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-[#64748B]">
                    {total} {" "}{translateInterfaceText("registro")}{total !== 1 ? 's' : ''} {" "}{translateInterfaceText("encontrado")}{total !== 1 ? 's' : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-9 rounded-[8px] border-[#E2E8F0]"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-[#64748B]">
                      {translateInterfaceText("Página")}{" "}{page} {" "}{translateInterfaceText("de")}{" "}{totalPages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="h-9 rounded-[8px] border-[#E2E8F0]"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}
